import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ParticleBackground from '@/components/base/ParticleBackground';

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return mobile;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => setReduced(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
}

export default function HeroSection() {
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const reducedMotion = useReducedMotion();

  // Only track scroll on desktop and when user hasn't requested reduced motion
  const shouldAnimate = !isMobile && !reducedMotion;

  useEffect(() => {
    if (!shouldAnimate) return;
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [shouldAnimate]);

  // On mobile or reduced-motion: all effects disabled, static presentation
  const parallaxY = shouldAnimate ? scrollY * 0.35 : 0;
  const contentOpacity = shouldAnimate ? Math.max(0, 1 - scrollY / 700) : 1;
  const contentTranslateY = shouldAnimate ? scrollY * 0.15 : 0;
  const scaleEffect = shouldAnimate ? 1 + scrollY * 0.0003 : 1;

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-screen flex items-center overflow-hidden"
    >
      {/* Parallax Background Image */}
      <div
        className={`absolute inset-0 ${shouldAnimate ? 'will-change-transform' : ''}`}
        style={{
          transform: shouldAnimate
            ? `translateY(${parallaxY}px) scale(${scaleEffect})`
            : 'none',
        }}
      >
        <img
          src="https://readdy.ai/api/search-image?query=Ancient%20Chinese%20Shanxi%20architecture%20outline%20with%20digital%20particle%20data%20flow%20effects%2C%20dark%20ink%20black%20background%20with%20golden%20geometric%20lines%2C%20futuristic%20technology%20meets%20traditional%20eastern%20culture%2C%20clean%20minimal%20composition%2C%20dramatic%20lighting%20with%20warm%20gold%20accents%2C%20no%20text%2C%20abstract%20cultural%20tech%20aesthetic%2C%20high%20contrast%2C%20professional%20corporate%20look&width=1920&height=1080&seq=hero-bg-jhxm-01&orientation=landscape"
          alt="晋铧叙梦"
          className={`object-cover object-top ${shouldAnimate ? 'h-[120%]' : 'h-full'} w-full opacity-70`}
        />
      </div>

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background-950/78 via-background-950/66 to-background-950/92 z-[1]"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-background-950/50 via-background-950/10 to-transparent z-[1]"></div>

      {/* Particle Animation */}
      <ParticleBackground compact />

      {/* Content — skip inline animation styles on mobile for perf */}
      <div
        className="relative z-10 w-full px-4 md:px-8 lg:px-12"
        style={
          shouldAnimate
            ? {
                opacity: contentOpacity,
                transform: `translateY(${contentTranslateY}px)`,
              }
            : undefined
        }
      >
        <div className="max-w-screen-2xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center min-h-screen pt-20 pb-10">
          {/* Left Content */}
          <div className="flex flex-col justify-center">
            {/* Tag badge — no pulse on mobile to reduce repaints */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-500/15 border border-accent-500/25 mb-8 w-fit">
              <span className={`w-1.5 h-1.5 rounded-full bg-accent-500 ${!isMobile ? 'animate-pulse' : ''}`}></span>
              <span className="text-accent-300 text-xs md:text-sm font-medium tracking-wider whitespace-nowrap">
                2026 · 深耕文化数字赛道 · 联盟协作赋能县域
              </span>
            </div>

            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-primary-500 mb-6 tracking-wide">
              晋铧叙梦
            </h1>

            <p className="text-foreground-100 text-lg md:text-2xl font-light mb-3 leading-relaxed">
              以AI重构地域文化数字资产
            </p>
            <p className="text-foreground-100/80 text-lg md:text-2xl font-light mb-3 leading-relaxed">
              冯全普·县域AI+OPC联盟核心成员单位
            </p>

            <p className="text-secondary-400 text-sm md:text-base mt-6 mb-10 max-w-lg leading-relaxed">
              让每一寸土地的文化记忆，在数字时代被留存、被看见、被传颂
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/about"
                className="group px-8 py-3.5 bg-foreground-50 text-background-950 font-medium rounded-full text-sm hover:bg-primary-400 hover:text-background-950 transition-all duration-300 text-center whitespace-nowrap cursor-pointer relative overflow-hidden"
              >
                <span className="relative z-10">了解我们</span>
                {!isMobile && (
                  <div className="absolute inset-0 bg-primary-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-full"></div>
                )}
              </Link>
              <Link
                to="/cooperation"
                className="group px-8 py-3.5 border border-primary-500/60 text-primary-500 font-medium rounded-full text-sm hover:bg-primary-500/10 transition-all duration-300 text-center whitespace-nowrap cursor-pointer relative overflow-hidden"
              >
                <span className="relative z-10 group-hover:text-foreground-50 transition-colors duration-300">业务合作</span>
                {!isMobile && (
                  <div className="absolute inset-0 bg-primary-500/20 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-full"></div>
                )}
              </Link>
              <Link
                to="/business"
                className="group px-8 py-3.5 border border-secondary-600/60 text-secondary-300 font-medium rounded-full text-sm hover:border-foreground-300 hover:text-foreground-100 transition-all duration-300 text-center whitespace-nowrap cursor-pointer"
              >
                获取方案
              </Link>
            </div>
          </div>

          {/* Right Side - Floating Decorative (desktop only) */}
          <div className="hidden xl:flex items-center justify-center relative opacity-80">
            <div className="relative w-full aspect-square max-w-lg">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary-500/4 to-accent-500/3 border border-primary-500/8 backdrop-blur-sm"></div>

              {!reducedMotion && (
                <>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 rounded-full bg-primary-500/5 animate-float"></div>
                  <div
                    className="absolute top-[15%] right-[10%] w-28 h-28 rounded-full bg-accent-500/5 animate-float"
                    style={{ animationDelay: '1.5s' }}
                  ></div>
                  <div
                    className="absolute bottom-[10%] left-[15%] w-36 h-36 rounded-full bg-primary-500/5 animate-float"
                    style={{ animationDelay: '3s' }}
                  ></div>
                  <div
                    className="absolute top-[35%] left-[5%] w-16 h-16 rounded-full bg-foreground-50/5 animate-float"
                    style={{ animationDelay: '4.5s' }}
                  ></div>
                </>
              )}

              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="font-heading text-8xl font-bold text-primary-500/30 mb-2">
                    117
                  </div>
                  <p className="text-secondary-400 text-sm tracking-wider">山西县域文化数字资产库</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator — hide on mobile when already scrolled */}
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 z-10 transition-opacity duration-300 ${isMobile && scrollY > 100 ? 'opacity-0' : ''}`}>
        <div className="w-6 h-10 rounded-full border border-secondary-600/50 flex items-start justify-center pt-2">
          <div className="w-1 h-2.5 bg-secondary-500 rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}
